/*
 * PAGE LOCK AND A STILL LAYER UNDER THE ON-SCREEN KEYBOARD (iOS Safari and other touch browsers).
 *
 * The iOS keyboard does not resize the page: it scrolls the document and shifts the visible part (visual
 * viewport) to "show" the field - `position: fixed` layers jump, layers sized to their content end up behind the
 * keyboard. This module, in one place for every layer of the storefront (touch only - a desktop has no on-screen
 * keyboard):
 *
 * 1. LOCK. While a layer with fields is open (its lock class on <html> or <body> - LOCK_CLASSES below and the theme's
 *    config.json "scroll_lock" - or by hand: ScrollLock.lock(key)), the <body> becomes `position: fixed; top:
 *    -scrollY` and the <html> keeps its old height and scroll: the page stays exactly where it was, and the iOS 26
 *    Safari bottom bar does not open (scrolled to 0 it would, and layers anchored to the bottom would jump 40px).
 *    On unlock everything is restored. The layers' own JS need not be touched: their class is watched.
 * 2. STILL LAYER. When a field in the layer gets focus, the layer (the outermost fixed ancestor of the field) is pinned
 *    to its current place in pixels (`top`, `height`), and what iOS shifts the visible part by
 *    (`visualViewport.offsetTop`) is given back to it, to the other visible fixed elements (menu, scrim) and to the
 *    <body>. The keyboard simply slides over the bottom of the picture. If the field would fall under the keyboard, the
 *    layer's own scroller scrolls (with a keyboard's worth of room at the bottom), not the layer.
 *    Pinning is released after the keyboard has closed.
 *
 * The engine's own layers use these classes: `country-field-open` (checkout country sheet), `co-layer-open`
 * (pickup point / login dialogs, the phone county list). A theme adds its layers in config.json:
 *   "scroll_lock": {"html": ["my-html-class"], "body": ["vv-search-open"], "skip": [".my-dock"]}
 * `skip`: fixed elements that must not be moved (a bottom dock, a browser-bar colour edge); [data-no-pin] always.
 */
(() => {
    const root = document.documentElement;
    const touch = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)');
    /* [element, class] - the field-carrying layers' own lock classes (the engine's, then the theme's config.json) */
    const CONFIG = window.SCROLL_LOCK || {};
    const LOCK_CLASSES = [
        ['html', 'country-field-open'],
        ['html', 'co-layer-open'],
        ...(CONFIG.html || []).map((c) => ['html', c]),
        ...(CONFIG.body || []).map((c) => ['body', c]),
    ];
    let locked = false;
    let y = 0;

    const keys = new Set();   // manual locks: ScrollLock.lock(key) / unlock(key)
    const wanted = () => !!touch && touch.matches && (keys.size > 0 || LOCK_CLASSES.some(([t, c]) => (t === 'html' ? root : document.body).classList.contains(c)));

    /* The document's HEIGHT and scroll position stay (the <html> gets the old height): if the scroll jumped to 0, the iOS 26
       Safari would open its bottom bar and layers anchored to the bottom would jump 40px. Scrolling is off meanwhile
       (overflow: hidden); what is seen is the fixed <body>. */
    const ROOT_PROPS = ['height', 'overflow'];
    const BODY_PROPS = ['position', 'top', 'left', 'right', 'width'];
    let rootSaved = null;
    const toTop = (v) => {
        if (Math.abs((window.scrollY || 0) - v) < 1) return;
        /* a smooth scroll (scroll-behavior: smooth) would look like a jump here */
        const sb = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, v);
        root.style.scrollBehavior = sb;
    };
    const lock = () => {
        const b = document.body;
        y = window.scrollY || root.scrollTop || 0;
        rootSaved = ROOT_PROPS.map((p) => [p, root.style.getPropertyValue(p)]);
        root.style.height = root.scrollHeight + 'px';
        root.style.overflow = 'hidden';
        b.style.position = 'fixed';
        b.style.top = -y + 'px';
        b.style.left = '0';
        b.style.right = '0';
        b.style.width = '100%';
        toTop(y);
        root.classList.add('vv-scroll-lock');
        locked = true;
    };
    const unlock = () => {
        doUnpin();
        const b = document.body;
        BODY_PROPS.forEach((p) => b.style.removeProperty(p));
        (rootSaved || []).forEach(([p, v]) => { if (v) root.style.setProperty(p, v); else root.style.removeProperty(p); });
        rootSaved = null;
        root.classList.remove('vv-scroll-lock');
        toTop(y);
        locked = false;
    };
    const sync = () => {
        if (!document.body) return;
        const w = wanted();
        if (w && !locked) lock();
        else if (!w && locked) unlock();
    };

    /* ── 2. STILL LAYER ── touch only: a desktop has no on-screen keyboard */
    const vv = window.visualViewport;
    /* fixed elements that stay where they are (the theme lists its own in config.json "scroll_lock.skip") */
    const SKIP = ['[data-no-pin]', ...(CONFIG.skip || [])].join(', ');
    const FIELD = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="hidden"]):not([type="range"]):not([type="color"]):not([type="file"]), textarea, [contenteditable=""], [contenteditable="true"]';
    const PROPS = ['top', 'bottom', 'height', 'max-height', 'min-height'];
    let pin = null;       // { layer, parts: [{ el, top, h, saved }], others: [{ el, v }], off0, pad }
    let unpinTimer = 0;

    const layerOf = (el) => {
        let n = el, last = null;
        while (n && n !== document.body && n !== root) {
            if (getComputedStyle(n).position === 'fixed') last = n;
            n = n.parentElement;
        }
        return last;
    };
    /* the layer's companions that stand with it: on the Tom Select phone sheet the search (`.ts-control`) and the list
       (`.ts-dropdown`) are two separate fixed elements */
    const partsOf = (L) => {
        const out = [L];
        const ts = L.matches('.ts-control') && L.closest('.ts-wrapper');
        const dd = ts && ts.querySelector('.ts-dropdown');
        if (dd && getComputedStyle(dd).position === 'fixed') out.push(dd);
        return out;
    };
    /* the other visible fixed elements (a menu under the layer, scrims, the header): these are only shifted (`translate`,
       their own `transform` is not touched) - up to 3 levels under the <body> */
    const othersOf = (parts) => {
        const all = [...document.body.querySelectorAll(':scope > *, :scope > * > *, :scope > * > * > *')].filter((el) => {
            if (parts.some((p) => p.contains(el) || el.contains(p)) || el.closest(SKIP)) return false;
            const cs = getComputedStyle(el);
            return cs.position === 'fixed' && cs.display !== 'none' && cs.visibility !== 'hidden';
        });
        return all.filter((el) => !all.some((o) => o !== el && o.contains(el)));   // a nested one must not slide twice
    };
    /* give back the shift of the visible part: the layer, the other fixed elements and (when locked) the page behind move by the
       same amount */
    const place = () => {
        if (!pin) return;
        const d = (vv ? vv.offsetTop : 0) - pin.off0;
        pin.parts.forEach((p) => p.el.style.setProperty('top', (p.top + d) + 'px', 'important'));
        pin.others.forEach((o) => { o.el.style.translate = d ? '0 ' + d + 'px' : o.v; });
        if (locked) document.body.style.top = (-y + d) + 'px';
    };
    /* if the field would fall under the keyboard (lower in the layer), the layer is not pushed up: its OWN scroller scrolls,
       with a keyboard's worth of room at the bottom, as in a native app (the sheet stands, its content scrolls under the
       field) */
    const scrollerOf = (el, L) => {
        for (let n = el.parentElement; n && n !== L.parentElement; n = n.parentElement) {
            const o = getComputedStyle(n).overflowY;
            if ((o === 'auto' || o === 'scroll') && n.clientHeight > 0) return n;
        }
        return null;
    };
    const reveal = () => {
        if (!pin || !vv) return;
        const a = document.activeElement;
        if (!a || !pin.layer.contains(a)) return;
        const visBottom = vv.offsetTop + vv.height - 12;
        const r = a.getBoundingClientRect();
        if (r.bottom <= visBottom) return;
        const sc = scrollerOf(a, pin.layer);
        if (!sc) return;
        if (!pin.pad) {
            const kb = Math.max(0, pin.layer.getBoundingClientRect().bottom - (vv.offsetTop + vv.height));
            pin.pad = { el: sc, v: sc.style.paddingBottom };
            sc.style.paddingBottom = ((parseFloat(getComputedStyle(sc).paddingBottom) || 0) + kb) + 'px';
        }
        sc.scrollTop += r.bottom - visBottom;
    };
    let revealRaf = 0;
    const onViewport = () => {
        place();
        if (!revealRaf) revealRaf = requestAnimationFrame(() => { revealRaf = 0; reveal(); });
    };
    const doPin = (L) => {
        pin = {
            layer: L,
            off0: vv ? vv.offsetTop : 0,
            /* offsetTop / offsetHeight: without the opening animation's transform */
            parts: partsOf(L).map((el) => ({
                el,
                top: el.offsetTop,
                h: el.offsetHeight,
                saved: PROPS.map((p) => [p, el.style.getPropertyValue(p), el.style.getPropertyPriority(p)]),
            })),
        };
        pin.others = othersOf(pin.parts.map((p) => p.el)).map((el) => ({ el, v: el.style.translate }));
        pin.parts.forEach(({ el, h }) => {
            el.style.setProperty('height', h + 'px', 'important');
            el.style.setProperty('bottom', 'auto', 'important');
            el.style.setProperty('max-height', 'none', 'important');
            el.style.setProperty('min-height', '0px', 'important');
        });
        place();
    };
    const doUnpin = () => {
        clearTimeout(unpinTimer);
        if (!pin) return;
        pin.parts.forEach(({ el, saved }) => {
            saved.forEach(([p, v, prio]) => { if (v) el.style.setProperty(p, v, prio); else el.style.removeProperty(p); });
        });
        pin.others.forEach((o) => { o.el.style.translate = o.v; });
        if (pin.pad) pin.pad.el.style.paddingBottom = pin.pad.v;
        if (locked) document.body.style.top = -y + 'px';
        pin = null;
    };
    const kbOpen = () => vv && window.innerHeight - vv.height > 80;

    document.addEventListener('focusin', (e) => {
        if (!touch || !touch.matches) return;
        const t = e.target;
        if (!(t instanceof Element) || !t.matches(FIELD) || t.closest(SKIP)) return;
        const tryPin = () => {
            if (document.activeElement !== t) return false;
            const L = layerOf(t);
            if (!L) return false;
            clearTimeout(unpinTimer);
            if (pin && pin.layer === L) { onViewport(); return true; }   // from field to field
            doUnpin();
            doPin(L);
            return true;
        };
        /* Tom Select lifts the field onto the fixed sheet only in its OWN focus handler - with no fixed ancestor yet, look
           again after the handlers (the keyboard starts later than that) */
        if (!tryPin()) setTimeout(tryPin, 0);
    }, true);
    document.addEventListener('focusout', () => {
        if (!pin) return;
        clearTimeout(unpinTimer);
        /* still pinned while the keyboard moves - released after it (~1 s at most) */
        let n = 0;
        const wait = () => {
            const a = document.activeElement;
            if (pin && a && a.matches && a.matches(FIELD) && pin.layer.contains(a)) return;   // from field to field
            if (pin && pin.layer.isConnected && kbOpen() && ++n < 20) { unpinTimer = setTimeout(wait, 50); return; }
            doUnpin();
        };
        unpinTimer = setTimeout(wait, 50);
    }, true);
    if (vv) {
        vv.addEventListener('scroll', onViewport);
        vv.addEventListener('resize', onViewport);
    }
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('orientationchange', doUnpin);

    const start = () => {
        const mo = new MutationObserver(sync);
        mo.observe(root, { attributes: true, attributeFilter: ['class'] });
        mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        sync();
    };
    if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);

    window.ScrollLock = {
        lock: (k) => { keys.add(k || 'x'); sync(); },
        unlock: (k) => { keys.delete(k || 'x'); sync(); },
        isLocked: () => locked,
    };
})();
